import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { Users, CheckSquare, Scale, Cpu } from 'lucide-react';

const OpenAIApproachSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Human alignment through RLHF and granular process supervision"
      >
        OpenAI: RLHF & Process Supervision
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <div className="slide-card mb-4 border-secondary/30">
              <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-secondary" />
                Core Philosophy
              </h3>
              <p className="text-muted-foreground text-sm">
                Align models with <strong className="text-secondary">human intent</strong> using 
                Reinforcement Learning from Human Feedback (RLHF). The InstructGPT pipeline uses 
                SFT followed by PPO-based RL trained on human rankings.
              </p>
            </div>
            
            <div className="slide-card">
              <h3 className="font-heading font-semibold text-foreground mb-4">Process vs Outcome Supervision</h3>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Scale className="w-4 h-4 text-muted-foreground" />
                    <span className="font-semibold text-muted-foreground">Outcome Supervision</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Judge only the final answer</p>
                  <div className="mt-2 p-2 rounded bg-background font-mono text-xs">
                    Problem → [hidden steps] → <span className="text-chart-4">Answer ✓/✗</span>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg bg-secondary/10 border border-secondary/30">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckSquare className="w-4 h-4 text-secondary" />
                    <span className="font-semibold text-secondary">Process Supervision</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-secondary/20 text-secondary">OpenAI</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Judge each intermediate step</p>
                  <div className="mt-2 p-2 rounded bg-background font-mono text-xs">
                    Problem → <span className="text-accent">Step1 ✓</span> → <span className="text-accent">Step2 ✓</span> → <span className="text-destructive">Step3 ✗</span> → ...
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <div className="slide-card mb-4">
              <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-primary" />
                O1 Series
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                While specific training details are proprietary, comparisons suggest:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  Heavy system-level risk control
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  High performance on reasoning benchmarks
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  Similar capabilities to DeepSeek-R1
                </li>
              </ul>
            </div>
            
            <div className="slide-card border-accent/30">
              <h3 className="font-heading font-semibold text-accent mb-3">Why Process Supervision?</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center text-xs text-accent">1</div>
                  <p className="text-sm text-muted-foreground"><strong className="text-foreground">More reliable</strong> for complex math tasks</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center text-xs text-accent">2</div>
                  <p className="text-sm text-muted-foreground"><strong className="text-foreground">Catches errors</strong> before they compound</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center text-xs text-accent">3</div>
                  <p className="text-sm text-muted-foreground"><strong className="text-foreground">Better credit assignment</strong> to reasoning steps</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 rounded-xl bg-secondary/10 border border-secondary/20">
          <p className="text-sm text-muted-foreground">
            <strong className="text-secondary">Trade-off:</strong> Process supervision requires more human annotation effort but creates more interpretable and reliable reasoning models.
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default OpenAIApproachSlide;
